/**
 * THE TOP BAR, MEASURED — one implementation for everything that has to
 * know how far down the page's own content begins.
 *
 * Lifted out of `components/froggerAI/FroggerDock.jsx` (which re-exports
 * it) in fix batch 33, R11 (Fable, 2026-10-07): the navbar's spacer was a
 * hard-coded 80px `marginTop` "for a fixed navbar", sized for a bar the app
 * does not draw — the navbar is `position: sticky` (index.css, navbar.css),
 * IN FLOW, so `<main>` already starts under it and the 80px was a band of
 * empty white on every page. The dock measured the bar; now the spacer
 * reads the same measurement instead of a second constant.
 *
 * Only a bar actually anchored to the TOP of the viewport counts (fixed or
 * sticky, top <= 2), so a sticky bar scrolled out of the way contributes
 * nothing.
 */
export const TOP_BAR_SELECTOR =
  '.custom-navbar, .navbar.fixed-top, [data-app-header="1"]';

/** The bars that actually sit against the top of the viewport, if any. */
export const topBarNodes = (doc = document, win = window) => {
  let nodes = [];
  try {
    nodes = Array.from(doc.querySelectorAll(TOP_BAR_SELECTOR));
  } catch (e) { return []; }
  return nodes.filter((el) => {
    const position = win.getComputedStyle
      ? win.getComputedStyle(el).position : '';
    if (position !== 'fixed' && position !== 'sticky') return false;
    const rect = el.getBoundingClientRect?.();
    return !!rect && !!rect.height && rect.top <= 2;
  });
};

export const measureTopBar = (doc = document, win = window) => (
  topBarNodes(doc, win).reduce(
    (offset, el) => Math.max(offset, Math.round(el.getBoundingClientRect().bottom)),
    0));

/**
 * HOW MUCH OF THE TOP BAR OVERLAPS WHERE THE PAGE'S CONTENT BEGINS — the
 * height a spacer standing at `spacerEl` must have so nothing is drawn
 * under the bar. `spacerEl`'s position is read in DOCUMENT coordinates (its
 * viewport top plus the scroll), so a sticky bar that has followed the
 * scroll is not mistaken for one covering the page's start:
 *
 *   - a STICKY (in-flow) bar: the spacer already sits at the bar's bottom,
 *     so the overlap is 0 — which is the navbar this app draws;
 *   - a FIXED (out-of-flow) bar: the spacer sits at 0, so the overlap is
 *     the bar's measured height.
 */
export const topBarOverlap = (spacerEl, doc = document, win = window) => {
  if (!spacerEl || !spacerEl.getBoundingClientRect) return 0;
  const bar = measureTopBar(doc, win);
  if (!bar) return 0;
  const scrollY = win.scrollY || win.pageYOffset || 0;
  const spacerTop = spacerEl.getBoundingClientRect().top + scrollY;
  return Math.max(0, Math.round(bar - spacerTop));
};

/**
 * HOW MUCH OF THE TOP BAR WILL COVER THE TOP OF THE VIEWPORT WHEN THE PAGE
 * IS SCROLLED TO ``scrollY`` (document px) — not now, THEN. Cowork run 44.
 *
 * The navbar is `position: sticky`, and a sticky box only rides the scroll
 * inside its PARENT. `.app-shell` is stretched to exactly the viewport's
 * height (`html, body, #root { height: 100% }`, `#root` a row flex), so the
 * bar stays pinned for roughly the first screen of scroll and is then
 * carried off the top with its parent. An anchor that lands a heading
 * under the bar in the first screen lands it flush against an EMPTY top
 * edge further down — so how far below the edge a heading must stop
 * depends on where it will be scrolled to. Measured on beta 2026-10-12.
 *
 * R5 B1 (2026-10-26) removed the clamp: `body` / `#root` now take the
 * viewport as a MINIMUM (index.css), so `.app-shell` is as tall as the page
 * and the bar covers its height at every scroll. This reads the bar's
 * parent rather than assuming either layout, so it stays right in both.
 *
 * A FIXED bar covers its own height at every scroll. A bar that is neither
 * covers nothing.
 */
export const topBarOverlapAt = (scrollY, doc = document, win = window) => {
  let nodes = [];
  try {
    nodes = Array.from(doc.querySelectorAll(TOP_BAR_SELECTOR));
  } catch (e) { return 0; }
  const nowY = win.scrollY || win.pageYOffset || 0;
  return nodes.reduce((overlap, el) => {
    const position = win.getComputedStyle
      ? win.getComputedStyle(el).position : '';
    const rect = el.getBoundingClientRect?.();
    if (!rect || !rect.height) return overlap;
    if (position === 'fixed') {
      return rect.top <= 2 ? Math.max(overlap, Math.round(rect.bottom)) : overlap;
    }
    if (position !== 'sticky' || !el.parentElement) return overlap;
    const box = el.parentElement.getBoundingClientRect();
    // Document coordinates. The bar's natural place is the top of its
    // parent (the navbar is the shell's first child).
    const naturalTop = box.top + nowY;
    const parentBottom = box.bottom + nowY;
    const top = Math.max(naturalTop - scrollY, 0);
    const bottom = Math.min(top + rect.height, parentBottom - scrollY);
    return Math.max(overlap, Math.round(Math.max(0, bottom)));
  }, 0);
};

/** The gap kept between the top bar's bottom (or the viewport's top, where
 *  no bar covers it) and a deep link's target. */
export const ANCHOR_GAP = 16;

/**
 * THE SCROLL MARGIN A DEEP LINK'S TARGET NEEDS so it stops just below
 * whatever of the top bar will be covering the viewport WHERE IT LANDS
 * (`topBarOverlapAt`) — lifted out of the care guide's TOC (Cowork run 44)
 * so every `scrollIntoView({ block: 'start' })` deep link clears the bar
 * the same way (R5 B1 review: the My Store product accordion landed its
 * header under the bar, which since R5 B1 stays at the top). Landing with
 * the gap alone leaves no bar over the target → the gap alone; otherwise
 * the bar's full height plus the gap.
 */
export const anchorMarginFor = (target, gap = ANCHOR_GAP, doc = document, win = window) => {
  if (!target || !target.getBoundingClientRect) return gap;
  const top = target.getBoundingClientRect().top + (win.scrollY || win.pageYOffset || 0);
  if (topBarOverlapAt(top - gap, doc, win) === 0) return gap;
  return gap + topBarOverlapAt(0, doc, win);
};
